// First-run guided wizard. Shows once after login (gated by localStorage),
// can be replayed via the user menu. Standalone DOM (does not reuse Modal.js
// because it has its own multi-step layout and progress UI).

const STORAGE_KEY = 'pm_wizard_completed_v1';

const STEPS = [
  {
    icon: 'fa-rocket',
    logo: true,
    color: 'blue',
    title: 'Welcome to Autovella',
    body: `
      <p class="text-gray-600 leading-relaxed">
        We'll walk you through the 6 things most teams set up first, then a quick lap
        around everything else. Each step has a <span class="font-semibold text-blue-600">Take me there</span>
        button that drops you on the right page — you can pause and resume the tour from
        the user menu (top-right) any time.
      </p>
      <p class="text-sm text-gray-500 mt-3">Takes about 5 minutes.</p>
    `,
  },
  {
    icon: 'fa-file-csv',
    color: 'sky',
    title: 'Already have data? Bring it with you',
    body: `
      <p class="text-gray-600 leading-relaxed">
        If you're moving from another tool, you can import Accounts, Contacts, Projects,
        Tasks, or Users from a CSV — we'll auto-detect the columns.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Each entity has a downloadable starter template</li>
        <li>You'll preview every row before anything is created</li>
        <li>Re-run later any time from the user menu (top-right) → <span class="font-medium">Import data</span></li>
      </ul>
    `,
    action: 'import',
    cta: 'Open import wizard',
  },
  {
    icon: 'fa-building',
    color: 'indigo',
    title: 'Step 1 — Add an Account',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Accounts are the companies you work with (your customers).
        Start here so projects and invoices have something to attach to.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">CRM → Accounts</span> in the sidebar</li>
        <li>Click <span class="font-medium">+ New Account</span></li>
        <li>Save — you'll come back to link contacts and projects to it</li>
      </ul>
    `,
    route: '/accounts',
    cta: 'Take me to Accounts',
  },
  {
    icon: 'fa-address-book',
    color: 'purple',
    title: 'Step 2 — Add a Contact',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Contacts are the people inside an account — the folks you'll email invoices to
        or share projects with.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">CRM → Contacts</span></li>
        <li>Click <span class="font-medium">+ New Contact</span> and pick the account</li>
      </ul>
    `,
    route: '/contacts',
    cta: 'Take me to Contacts',
  },
  {
    icon: 'fa-folder-plus',
    color: 'emerald',
    title: 'Step 3 — Create a Project',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Projects are the unit of work. Set a name, dates, and link the account.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Work → Projects</span></li>
        <li>Click <span class="font-medium">+ New Project</span></li>
        <li>Once created, open the project to add tasks and team members</li>
      </ul>
    `,
    route: '/projects',
    cta: 'Take me to Projects',
  },
  {
    icon: 'fa-list-check',
    color: 'amber',
    title: 'Step 4 — Add Tasks',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Break the project into tasks. Assign owners, due dates, and watch the
        Gantt or Kanban update automatically.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Work → Tasks</span></li>
        <li>Or open the project and use the inline task list</li>
        <li>Use <span class="font-medium">Gantt Chart</span> to visualize timelines</li>
      </ul>
    `,
    route: '/tasks',
    cta: 'Take me to Tasks',
  },
  {
    icon: 'fa-clock',
    color: 'sky',
    title: 'Step 5 — Log Time',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Track hours against tasks. These entries roll up into reports and
        feed directly into invoices.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Work → Timesheets</span></li>
        <li>Pick a project + task, enter hours, save</li>
      </ul>
    `,
    route: '/timesheets',
    cta: 'Take me to Timesheets',
  },
  {
    icon: 'fa-file-invoice-dollar',
    color: 'rose',
    title: 'Step 6 — Send an Invoice',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Pull tracked time and expenses into an invoice, then export or email it.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Finance → Invoices</span></li>
        <li>Click <span class="font-medium">+ New Invoice</span> and pick the account</li>
      </ul>
    `,
    route: '/invoices',
    cta: 'Take me to Invoices',
  },
  {
    icon: 'fa-percent',
    color: 'amber',
    title: 'GST & multi-country tax',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Invoicing customers in different countries? Set a default tax title per customer
        country in <span class="font-medium">Settings → Invoicing</span>.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Invoicing in India? Set your company GSTIN and state there too</li>
        <li>CGST/SGST, IGST, or LUT is then calculated per line item from HSN/SAC codes automatically</li>
        <li>Manage HSN/SAC rate codes any time from the same screen</li>
      </ul>
    `,
  },
  {
    icon: 'fa-handshake',
    color: 'indigo',
    title: 'Track your sales pipeline with Deals',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Deals track sales opportunities against an Account, from first contact through close.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">CRM → Deals</span></li>
        <li>Move deals through stages on the pipeline board</li>
      </ul>
    `,
    route: '/deals',
    cta: 'Take me to Deals',
  },
  {
    icon: 'fa-chart-gantt',
    color: 'purple',
    title: 'Plan with Sprints & Gantt',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Once tasks exist, group them into sprints or lay them out on a timeline.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li><span class="font-medium">Sprints</span> — plan work in fixed iterations</li>
        <li><span class="font-medium">Gantt Chart</span> — visualize dependencies and deadlines</li>
      </ul>
    `,
    route: '/sprints',
    cta: 'Take me to Sprints',
  },
  {
    icon: 'fa-chart-bar',
    color: 'sky',
    title: 'See it all in Analytics',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Cross-project dashboards for progress, workload, and financials — no spreadsheet exports needed.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Work → Analytics</span></li>
        <li>Filter by project, team, or date range</li>
      </ul>
    `,
    route: '/analytics',
    cta: 'Take me to Analytics',
  },
  {
    icon: 'fa-receipt',
    color: 'emerald',
    title: 'Expenses & Approvals',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Log spend against a project, then route it through approval before it hits an invoice.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Finance → Expenses</span> to log a cost</li>
        <li>Track pending sign-off in <span class="font-medium">Finance → Approvals</span></li>
      </ul>
    `,
    route: '/expenses',
    cta: 'Take me to Expenses',
  },
  {
    icon: 'fa-calendar-minus',
    color: 'amber',
    title: 'Time Off, Attendance & Calendar',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Keep capacity realistic before you commit to deadlines.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li><span class="font-medium">Time Off</span> — leave requests &amp; sign-off</li>
        <li><span class="font-medium">Attendance</span> — daily check-ins</li>
        <li><span class="font-medium">Calendar &amp; Holidays</span> — org-wide schedule</li>
      </ul>
    `,
    route: '/time-off',
    cta: 'Take me to Time Off',
  },
  {
    icon: 'fa-robot',
    color: 'blue',
    title: 'Automate the busywork',
    body: `
      <p class="text-gray-600 leading-relaxed">
        Trigger actions automatically on task or project events — status changes, due dates,
        assignments — instead of doing them by hand.
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li>Open <span class="font-medium">Work → Automations</span></li>
        <li>Click <span class="font-medium">+ New Automation</span> and pick a trigger</li>
      </ul>
    `,
    route: '/automations',
    cta: 'Take me to Automations',
  },
  {
    icon: 'fa-circle-check',
    color: 'green',
    title: 'You\'re all set',
    body: `
      <p class="text-gray-600 leading-relaxed">
        That covers every module. One last stop worth knowing about:
      </p>
      <ul class="mt-3 space-y-1.5 text-sm text-gray-600 list-disc list-inside">
        <li><span class="font-medium">Settings</span> — invite teammates and configure your org</li>
      </ul>
      <p class="text-sm text-gray-500 mt-4">
        You can replay this tour any time from the user menu (top-right) → <span class="font-medium">Take the tour</span>.
      </p>
    `,
  },
];

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-600',    ring: 'ring-blue-200',    btn: 'bg-blue-600 hover:bg-blue-700' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  ring: 'ring-indigo-200',  btn: 'bg-indigo-600 hover:bg-indigo-700' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-600',  ring: 'ring-purple-200',  btn: 'bg-purple-600 hover:bg-purple-700' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   ring: 'ring-amber-200',   btn: 'bg-amber-600 hover:bg-amber-700' },
  sky:     { bg: 'bg-sky-50',     text: 'text-sky-600',     ring: 'ring-sky-200',     btn: 'bg-sky-600 hover:bg-sky-700' },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-600',    ring: 'ring-rose-200',    btn: 'bg-rose-600 hover:bg-rose-700' },
  green:   { bg: 'bg-green-50',   text: 'text-green-600',   ring: 'ring-green-200',   btn: 'bg-green-600 hover:bg-green-700' },
};

class WelcomeWizard {
  constructor(router) {
    this.router = router;
    this.index = 0;
    this.escHandler = null;
  }

  static maybeShow(router) {
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;
    new WelcomeWizard(router).open();
  }

  static show(router) {
    new WelcomeWizard(router).open();
  }

  static reset() {
    localStorage.removeItem(STORAGE_KEY);
  }

  open() {
    this.render();
    this.escHandler = (e) => { if (e.key === 'Escape') this.finish(); };
    document.addEventListener('keydown', this.escHandler);
  }

  render() {
    const step = STEPS[this.index];
    const total = STEPS.length;
    const c = COLOR_MAP[step.color] || COLOR_MAP.blue;
    const isFirst = this.index === 0;
    const isLast = this.index === total - 1;

    const dots = STEPS.map((_, i) => {
      const active = i === this.index;
      const done = i < this.index;
      const cls = active
        ? 'w-6 bg-blue-600'
        : done
          ? 'w-2 bg-blue-400'
          : 'w-2 bg-gray-300';
      return `<span class="h-2 rounded-full transition-all ${cls}"></span>`;
    }).join('');

    const html = `
      <div class="fixed inset-0 z-50 overflow-y-auto" id="wizard-overlay">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div class="fixed inset-0 transition-opacity bg-gray-500/75" id="wizard-backdrop"></div>
          <div class="relative z-10 inline-block max-w-lg w-full my-8 text-left align-middle transform bg-white shadow-xl rounded-xl flex flex-col" style="max-height:90vh">
            <div class="flex items-center justify-between px-6 pt-5 pb-3 border-b">
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500">Step ${this.index + 1} of ${total}</span>
              </div>
              <button type="button" class="text-gray-400 hover:text-gray-600" id="wizard-close-btn" title="Close">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="px-6 pt-2 pb-3 flex items-center gap-1.5">${dots}</div>

            <div class="px-6 py-4 overflow-y-auto flex-1">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-full ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0 ring-4 ${c.ring}">
                  ${step.logo
                    ? `<img src="/assets/images/autovella-mark.svg" alt="Autovella" class="w-8 h-8">`
                    : `<i class="fas ${step.icon} text-lg"></i>`}
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">${step.title}</h3>
                  ${step.body}
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button type="button" id="wizard-skip-btn" class="text-sm text-gray-500 hover:text-gray-700">
                ${isLast ? '' : 'Skip tour'}
              </button>
              <div class="flex items-center gap-2">
                <button type="button" id="wizard-back-btn" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 ${isFirst ? 'invisible' : ''}">
                  <i class="fas fa-chevron-left mr-1 text-xs"></i>Back
                </button>
                ${step.route ? `
                  <button type="button" id="wizard-goto-btn" class="px-3 py-1.5 text-sm text-white rounded-lg ${c.btn}">
                    <i class="fas fa-arrow-right mr-1 text-xs"></i>${step.cta || 'Take me there'}
                  </button>
                ` : ''}
                ${step.action === 'import' ? `
                  <button type="button" id="wizard-import-btn" class="px-3 py-1.5 text-sm text-white rounded-lg ${c.btn}">
                    <i class="fas fa-file-csv mr-1 text-xs"></i>${step.cta || 'Open import wizard'}
                  </button>
                ` : ''}
                <button type="button" id="wizard-next-btn" class="px-3 py-1.5 text-xs text-white bg-gray-900 hover:bg-gray-800 rounded-lg">
                  ${isLast ? 'Finish' : 'Next'}<i class="fas fa-chevron-right ml-1 text-xs"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const container = document.getElementById('modal-container');
    if (!container) return;
    container.insertAdjacentHTML('beforeend', html);

    this.attachListeners();
  }

  attachListeners() {
    document.getElementById('wizard-close-btn')?.addEventListener('click', () => this.finish());
    document.getElementById('wizard-backdrop')?.addEventListener('click', () => this.finish());
    document.getElementById('wizard-skip-btn')?.addEventListener('click', () => this.finish());
    document.getElementById('wizard-back-btn')?.addEventListener('click', () => this.go(this.index - 1));
    document.getElementById('wizard-next-btn')?.addEventListener('click', () => {
      if (this.index === STEPS.length - 1) this.finish();
      else this.go(this.index + 1);
    });
    document.getElementById('wizard-goto-btn')?.addEventListener('click', () => {
      const step = STEPS[this.index];
      this.finish();
      if (step.route && this.router) this.router.navigate(step.route);
      else if (step.route) window.location.href = step.route;
    });
    document.getElementById('wizard-import-btn')?.addEventListener('click', () => {
      this.finish();
      if (this.router) this.router.navigate('/import');
      else window.location.href = '/import';
    });
  }

  go(i) {
    if (i < 0 || i >= STEPS.length) return;
    this.index = i;
    this.removeOverlay();
    this.render();
  }

  removeOverlay() {
    document.getElementById('wizard-overlay')?.remove();
  }

  finish() {
    localStorage.setItem(STORAGE_KEY, 'true');
    this.removeOverlay();
    if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
  }
}

export default WelcomeWizard;
